import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormControl } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { ReactEditor } from 'slate-react';

import {
  CustomSlate,
  focusEditor,
  htmlToSlate,
  PROMPT_MENU_TYPE,
  RichSlateBrandBriefsContext,
  useRichSlateEditor,
} from 'src/components/RichSlate';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { createEditorInstanceImperativeHandle, EMPTY_PARAGRAPH_HTML } from 'src/lib/slate';

import { Footer } from './components/Footer';
import { InstructionsTextarea } from './components/InstructionsTextarea';
import { PromptMenu } from './components/PromptMenu';
import { useSupportPasteEvent } from './lib';
import { useHandlers } from './useHandlers';
import { useMenuSupport } from './useMenuSupport';
import { useStyles } from './useStyles';

const DEFAULT_MENU_PROPS = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  autoFocus: true,
};

export const PromptInstructions = forwardRef(
  (
    {
      className,
      value,
      onChange,
      disabled,
      isSuggestPromptLoading,
      brandBriefs,
      brandBriefsMenuItems,
      hideActions,
      editableClasses,
      generationTool,
      urlResources,
      imageResources,
      audioResources,
      videoResources,
      onEditorFocus,
      freestyleToolId,
      shortExpanding,
      noTags,
      disableAutoFocus,
      placeholder,
      renderPlaceholder,
      isFirstField,
      plusButtonRef,
      setPlusButtonRef,
      noMinMaxHeight,
      isUploadingImage,
      isApplicationModeInfinity,
      hideImages,
      isUploadingDocument,
      documentResources,
      index,
      shouldOpenConfirmPopup,
      disablePaste,
      isPersonalizedPrompt,
      ratingsCount,
      showPersonalizePrompt,
      isSlim,
      uploadingDocuments,
      uploadingImages,
      editorDisabled,
      externalChannelMenuProps,
      onAction,
      hideContinueRowFloatingPanel,
      variables,
      hideAttachments,
      showVariables,
      showAddButton,
      instructions,
      showInstructions,
      disableKeyUpAction,
      isPromptField,
      displayPromptMenuAlternativeTitle,
      footerActionsSlot,
      shouldIncludeAudioVideoResources,
    },
    ref
  ) => {
    const styles = useStyles();
    const [menuType, setMenuType] = useState(PROMPT_MENU_TYPE.default);
    const [isFirstRefVisible, setIsFirstRefVisible] = useState(false);

    const [rootElement, setRootElement] = useState(null);
    const firstFieldRef = useRef();
    const menuRef = useRef(null);

    const {
      menuAnchor,
      menuVisible,
      handleCloseMenu,
      handleMenuButtonClick,
      menuProps,
      handleMenuPropsChange,
      setWasOpened,
      searchValue,
      setSearchValue,
      quickSearchMode,
      handleQuickSearchTriggered,
    } = useMenuSupport({ initMenuProps: DEFAULT_MENU_PROPS, onAction });

    const editor = useRichSlateEditor();
    const [initialValue] = useState(() => htmlToSlate(value || EMPTY_PARAGRAPH_HTML));

    useEffect(() => {
      // when tool id changes, focus at end
      if (!ReactEditor.isFocused(editor) && !disableAutoFocus) {
        focusEditor({ editor, atEnd: true });
        onEditorFocus?.();
      }
    }, [freestyleToolId, editor, disableAutoFocus, onEditorFocus]);

    useEffect(() => {
      // focus first prompt instructions field
      if (isFirstRefVisible) {
        if (!disableAutoFocus) {
          focusEditor({ editor, atEnd: true });
        }
        setIsFirstRefVisible(false);
      }
    }, [editor, isFirstRefVisible, disableAutoFocus]);

    const { handleKeyDown, handleOpenMenuAtCursor, handleKeyDownMenu, handleChange, handleAction } =
      useHandlers({
        editor,
        handleMenuButtonClick,
        handleMenuPropsChange,
        handleCloseMenu,
        menuRef,
        shouldOpenConfirmPopup,
        onChange,
        onAction,
      });

    useSupportPasteEvent({
      rootElement,
      disablePaste,
      shouldOpenConfirmPopup,
      index,
      onAction,
    });

    useImperativeHandle(ref, () => createEditorInstanceImperativeHandle(editor), [editor]);

    return (
      <div
        onClick={(event) => {
          if (event.currentTarget === event.target || event.currentTarget.contains(event.target)) {
            // process only if the click is on this element or its descendant
            onEditorFocus?.();
          }
        }}
        ref={(el) => {
          setRootElement(el);
          if (isFirstField && !firstFieldRef?.current) {
            firstFieldRef.current = el;
            setIsFirstRefVisible(!!el);
          }
        }}
      >
        <FormControl
          className={cx(styles.root, className, {
            [styles.disabled]: disabled,
            [styles.editorDisabled]: editorDisabled,
          })}
        >
          <RichSlateBrandBriefsContext.Provider value={brandBriefs}>
            <CustomSlate
              editor={editor}
              initialValue={initialValue}
              onValueChange={handleChange}
            >
              <InstructionsTextarea
                classes={editableClasses}
                onMenuButtonClick={handleMenuButtonClick}
                setWasOpened={setWasOpened}
                editor={editor}
                onCloseMenu={handleCloseMenu}
                onKeyDown={handleKeyDown}
                onEditorFocus={onEditorFocus}
                setMenuType={setMenuType}
                shortExpanding={shortExpanding}
                noTags={noTags}
                placeholder={placeholder}
                renderPlaceholder={renderPlaceholder}
                footerShown={!hideActions && !disabled}
                plusButtonRef={plusButtonRef}
                setPlusButtonRef={setPlusButtonRef}
                isFirstField={isFirstField}
                noMinMaxHeight={noMinMaxHeight}
                onMenuOpen={handleOpenMenuAtCursor}
                onQuickSearchTriggered={handleQuickSearchTriggered}
                disabled={editorDisabled}
                hideContinueRowFloatingPanel={hideContinueRowFloatingPanel}
                disableKeyUpAction={disableKeyUpAction}
                isPromptField={isPromptField}
              />
            </CustomSlate>
          </RichSlateBrandBriefsContext.Provider>

          {!disabled && (
            <Footer
              isSuggestPromptLoading={isSuggestPromptLoading}
              promptText={value}
              hideActions={hideActions}
              index={index}
              isPersonalizedPrompt={isPersonalizedPrompt}
              ratingsCount={ratingsCount}
              generationTool={generationTool}
              showPersonalizePrompt={showPersonalizePrompt}
              showImprovePrompt={generationTool !== GENERATION_TOOL.imageWithDescription}
              isSlim={isSlim}
              uploadingDocuments={uploadingDocuments}
              uploadingImages={uploadingImages}
              onAction={handleAction}
              variables={variables}
              hideAttachments={hideAttachments}
              showVariables={showVariables}
              onAddClick={handleMenuButtonClick}
              showAddButton={showAddButton}
              instructions={instructions}
              showInstructions={showInstructions}
              footerActionsSlot={footerActionsSlot}
            />
          )}

          <PromptMenu
            menuAnchor={menuAnchor}
            menuVisible={menuVisible}
            menuProps={menuProps}
            ref={menuRef}
            menuType={menuType}
            setMenuType={setMenuType}
            onCloseMenu={handleCloseMenu}
            onKeyDownMenu={handleKeyDownMenu}
            noTags={noTags}
            urlResources={urlResources}
            imageResources={imageResources}
            audioResources={audioResources}
            videoResources={videoResources}
            brandBriefsMenuItems={brandBriefsMenuItems}
            isApplicationModeInfinity={isApplicationModeInfinity}
            isUploadingImage={isUploadingImage}
            hideImages={hideImages}
            documentResources={documentResources}
            uploadingResources={[...uploadingDocuments, ...uploadingImages]}
            isUploadingDocument={isUploadingDocument}
            index={index}
            shouldOpenConfirmPopup={shouldOpenConfirmPopup}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            quickSearchMode={quickSearchMode}
            externalChannelMenuProps={externalChannelMenuProps}
            onAction={handleAction}
            displayAlternativeTitle={displayPromptMenuAlternativeTitle}
            shouldIncludeAudioVideoResources={shouldIncludeAudioVideoResources}
          />
        </FormControl>
      </div>
    );
  }
);

PromptInstructions.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSuggestPromptLoading: PropTypes.bool,
  brandBriefs: PropTypes.arrayOf(PropTypes.object),
  brandBriefsMenuItems: PropTypes.arrayOf(PropTypes.object),
  hideActions: PropTypes.bool,
  editableClasses: PropTypes.object,
  generationTool: PropTypes.string,
  urlResources: PropTypes.arrayOf(PropTypes.object),
  imageResources: PropTypes.arrayOf(PropTypes.object),
  audioResources: PropTypes.arrayOf(PropTypes.object),
  videoResources: PropTypes.arrayOf(PropTypes.object),
  onEditorFocus: PropTypes.func,
  freestyleToolId: PropTypes.string,
  shortExpanding: PropTypes.bool,
  noTags: PropTypes.bool,
  disableAutoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  renderPlaceholder: PropTypes.func,
  isFirstField: PropTypes.bool,
  plusButtonRef: PropTypes.object,
  setPlusButtonRef: PropTypes.func,
  noMinMaxHeight: PropTypes.bool,
  isUploadingImage: PropTypes.bool,
  isApplicationModeInfinity: PropTypes.bool,
  hideImages: PropTypes.bool,
  isUploadingDocument: PropTypes.bool,
  documentResources: PropTypes.arrayOf(PropTypes.object),
  index: PropTypes.number,
  shouldOpenConfirmPopup: PropTypes.func.isRequired,
  disablePaste: PropTypes.bool,
  isPersonalizedPrompt: PropTypes.bool,
  ratingsCount: PropTypes.shape(),
  showPersonalizePrompt: PropTypes.bool,
  isSlim: PropTypes.bool,
  uploadingDocuments: PropTypes.arrayOf(PropTypes.object),
  uploadingImages: PropTypes.arrayOf(PropTypes.object),
  editorDisabled: PropTypes.bool,
  externalChannelMenuProps: PropTypes.shape({}),
  onAction: PropTypes.func.isRequired,
  hideContinueRowFloatingPanel: PropTypes.bool,
  variables: PropTypes.arrayOf(PropTypes.object),
  hideAttachments: PropTypes.bool,
  showVariables: PropTypes.bool,
  showAddButton: PropTypes.bool,
  instructions: PropTypes.arrayOf(PropTypes.object),
  showInstructions: PropTypes.bool,
  disableKeyUpAction: PropTypes.bool,
  isPromptField: PropTypes.bool,
  displayPromptMenuAlternativeTitle: PropTypes.bool,
  footerActionsSlot: PropTypes.node,
  shouldIncludeAudioVideoResources: PropTypes.bool,
};

PromptInstructions.defaultProps = {
  className: null,
  value: undefined,
  disabled: false,
  isSuggestPromptLoading: false,
  brandBriefs: [],
  brandBriefsMenuItems: [],
  hideActions: false,
  editableClasses: null,
  generationTool: null,
  urlResources: [],
  imageResources: [],
  audioResources: [],
  videoResources: [],
  onEditorFocus: null,
  freestyleToolId: null,
  shortExpanding: false,
  noTags: false,
  disableAutoFocus: false,
  placeholder: null,
  renderPlaceholder: undefined,
  isFirstField: false,
  plusButtonRef: null,
  setPlusButtonRef: null,
  noMinMaxHeight: false,
  isUploadingImage: false,
  isApplicationModeInfinity: false,
  hideImages: false,
  isUploadingDocument: false,
  documentResources: [],
  index: 0,
  disablePaste: false,
  isPersonalizedPrompt: false,
  ratingsCount: null,
  showPersonalizePrompt: false,
  isSlim: false,
  uploadingDocuments: [],
  uploadingImages: [],
  editorDisabled: false,
  externalChannelMenuProps: null,
  hideContinueRowFloatingPanel: false,
  variables: [],
  hideAttachments: false,
  showVariables: false,
  showAddButton: false,
  instructions: [],
  showInstructions: false,
  disableKeyUpAction: false,
  isPromptField: true,
  displayPromptMenuAlternativeTitle: false,
  footerActionsSlot: null,
  shouldIncludeAudioVideoResources: false,
};
